/**
 * Hoodrank API client — all data comes from the Express backend.
 * Identical GETs for the same handle are cached in-session (~5 min).
 */

import { cachedGet, invalidateClientCache } from "./query-cache";
import type {
  About,
  Follower,
  Mention,
  Page,
  RankProfile,
  RankUser,
  SiteStats,
  TrendingAccount,
  Tweet,
} from "./types";

const API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "") ??
  (typeof window !== "undefined" ? "" : "http://localhost:8787");

/** Shown for any backend / network / rate-limit failure — never leak internals. */
export const FRIENDLY_API_ERROR =
  "Something went wrong, try later — everything will be alright.";

type FetchOpts = { force?: boolean };

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  try {
    const res = await fetch(`${API_BASE}/api${path}`, {
      ...init,
      headers: {
        Accept: "application/json",
        ...(init?.headers ?? {}),
      },
    });
    const text = await res.text();
    let data: unknown = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(FRIENDLY_API_ERROR);
      }
    }
    if (!res.ok) throw new Error(FRIENDLY_API_ERROR);
    return data as T;
  } catch (e) {
    if (e instanceof Error && e.message === FRIENDLY_API_ERROR) throw e;
    throw new Error(FRIENDLY_API_ERROR);
  }
}

function getCached<T>(path: string, opts?: FetchOpts): Promise<T> {
  return cachedGet(path, () => api<T>(path), { force: opts?.force });
}

function assertRankProfile(data: unknown): RankProfile {
  if (
    !data ||
    typeof data !== "object" ||
    !("user" in data) ||
    !(data as { user?: unknown }).user ||
    typeof (data as { user: { username?: unknown } }).user.username !== "string"
  ) {
    throw new Error(FRIENDLY_API_ERROR);
  }
  return data as RankProfile;
}

export async function fetchRankProfile(
  input: string,
  opts?: FetchOpts,
): Promise<RankProfile> {
  const path = `/rank/${encodeURIComponent(input)}`;
  return assertRankProfile(await getCached(path, opts));
}

export async function fetchPreviewProfile(): Promise<RankProfile> {
  return assertRankProfile(await getCached("/preview"));
}

export async function fetchTrending(): Promise<TrendingAccount[]> {
  return getCached<TrendingAccount[]>("/trending");
}

export async function fetchFollowers(
  username: string,
  page = 1,
  opts?: FetchOpts,
): Promise<Page<Follower>> {
  return getCached(
    `/profile/${encodeURIComponent(username)}/followers?page=${page}`,
    opts,
  );
}

export async function fetchFollowing(
  username: string,
  page = 1,
  opts?: FetchOpts,
): Promise<Page<Follower>> {
  return getCached(
    `/profile/${encodeURIComponent(username)}/following?page=${page}`,
    opts,
  );
}

export async function fetchVerifiedFollowers(
  username: string,
  page = 1,
  opts?: FetchOpts,
): Promise<Page<Follower>> {
  return getCached(
    `/profile/${encodeURIComponent(username)}/verified-followers?page=${page}`,
    opts,
  );
}

export async function fetchNewFollowers7d(
  username: string,
  opts?: FetchOpts,
): Promise<Follower[]> {
  return getCached(
    `/profile/${encodeURIComponent(username)}/new-followers-7d`,
    opts,
  );
}

export async function fetchNewFollowing7d(
  username: string,
  opts?: FetchOpts,
): Promise<Follower[]> {
  return getCached(
    `/profile/${encodeURIComponent(username)}/new-following-7d`,
    opts,
  );
}

export async function fetchAbout(
  username: string,
  opts?: FetchOpts,
): Promise<About> {
  return getCached(`/profile/${encodeURIComponent(username)}/about`, opts);
}

export async function fetchUserTweets(
  username: string,
  page = 1,
  opts?: FetchOpts,
): Promise<Page<Tweet>> {
  return getCached(
    `/feed/${encodeURIComponent(username)}/tweets?page=${page}`,
    opts,
  );
}

export async function fetchMentions(
  username: string,
  page = 1,
  _pageSize = 8,
  order: "popular" | "latest" = "latest",
  opts?: FetchOpts,
): Promise<Page<Mention>> {
  return getCached(
    `/feed/${encodeURIComponent(username)}/mentions?page=${page}&order=${order}`,
    opts,
  );
}

export async function fetchSiteStats(): Promise<SiteStats> {
  return getCached<SiteStats>("/stats");
}

export async function searchUsers(query: string): Promise<RankUser[]> {
  const q = query.trim().replace(/^@/, "");
  if (!q || q.length < 2) return [];
  try {
    // Autocomplete stays fresh — no long cache.
    const data = await api<{ users: RankUser[] }>(
      `/search/users?q=${encodeURIComponent(q)}`,
    );
    return data.users ?? [];
  } catch {
    return [];
  }
}

/** Does `follower` follow `target`? */
export async function checkFollow(
  target: string,
  follower: string,
): Promise<{ follow: boolean; user_protected: boolean }> {
  const t = target.trim().replace(/^@/, "");
  const f = follower.trim().replace(/^@/, "");
  if (!t || !f) return { follow: false, user_protected: false };
  return api<{ follow: boolean; user_protected: boolean }>(
    `/check/follow?user1=${encodeURIComponent(t)}&user2=${encodeURIComponent(f)}`,
  );
}

export { compactCount } from "./types";
export { invalidateClientCache };
